import { cn } from '@/lib/utils';
import { PromotionalCard } from '@/components/sections/PromotionalCard';
import type { ToolCardContainerProps } from '@/types/content';

/**
 * ToolCardContainer - Section component that displays a heading and a grid of
 * PromotionalCard components for the Umbrella Landing page.
 *
 * Renders the "I want to search for:" section with three tool cards
 * (Quality Care Navigator, Healthy Aging Resource Hub, Aging Data Explorer)
 * on a light gray background (#F2F2F2).
 *
 * @example
 * ```tsx
 * import { ToolCardContainer } from '@/components';
 *
 * <ToolCardContainer
 *   content={{
 *     sectionTitle: "I want to search for:",
 *     cards: [
 *       { title: "Quality Care Navigator", bodyText: "...", image: { src: "...", alt: "..." }, ctaLabel: "Go to tool", ctaHref: "/coming-soon" },
 *     ]
 *   }}
 * />
 * ```
 */
export function ToolCardContainer({ content, className }: ToolCardContainerProps) {
  if (!content) {
    return null;
  }

  return (
    <section
      className={cn(
        'bg-[#F2F2F2] py-10 px-4 sm:px-6 lg:px-10',
        className
      )}
    >
      <div className="max-w-[1360px] mx-auto flex flex-col items-center gap-8">
        {/* Section Title */}
        <h2
          className="font-semibold text-[24px] leading-[32px] text-black font-['Source_Sans_Pro'] text-center"
        >
          {content.sectionTitle}
        </h2>

        {/* Cards Container */}
        <div className="flex flex-col lg:flex-row justify-center gap-8 w-full">
          {content.cards.map((card, index) => (
            <PromotionalCard
              key={index}
              content={card}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

ToolCardContainer.displayName = 'ToolCardContainer';
